/**
 * Misspelling detection utility for JotDoc markdown content
 */

import type { ModalType } from '../../../components/modals/constants';

export interface MisspellingDetectionResult {
  misspelledMatches: string[];
  correctSpellingMatches: string[];
  spellingMap: { [correctSpelling: string]: string[] };
}

/**
 * Detects misspellings in markdown content using troubleWords patterns
 * Based on NoteViewRevamp.tsx lines 1726-1773
 */
export const detectMisspellingsInMarkdown = (
  markdown: string,
  troubleWords: Array<{ correct_spelling: string; misspellings: string[] }>
): MisspellingDetectionResult => {
  // Helper to escape regex special characters
  const escapeRegExp = (string: string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const misspelledMatches: string[] = [];
  const correctSpellingMatches: string[] = [];
  const spellingMap: { [correctSpelling: string]: string[] } = {};

  if (!troubleWords || troubleWords.length === 0) {
    return { misspelledMatches, correctSpellingMatches, spellingMap };
  }

  troubleWords.forEach(troubleWord => {
    const correctSpelling = troubleWord.correct_spelling;
    
    troubleWord.misspellings.forEach((misspelling: string) => {
      // Match words bounded by spaces, punctuation, or start/end of string
      const regex = new RegExp(
        `(?<=^|\\s|[.,!?;:()\\[\\]\\-/'"])(${escapeRegExp(
          misspelling,
        )})(?=\\s|[.,!?;:()\\[\\]\\-/'"]|$)`,
        'gi',
      );
      const matches = markdown.match(regex);
      
      if (matches && !misspelledMatches.includes(misspelling.toLowerCase())) {
        misspelledMatches.push(misspelling.toLowerCase());
        if (!correctSpellingMatches.includes(correctSpelling)) {
          correctSpellingMatches.push(correctSpelling);
          spellingMap[correctSpelling] = [];
        }
        if (!spellingMap[correctSpelling].includes(misspelling)) {
          spellingMap[correctSpelling].push(misspelling);
        }
      }
    });
  });

  return { misspelledMatches, correctSpellingMatches, spellingMap };
};

/**
 * Creates a word click handler for opening find/replace modal with misspelling context
 */
export const createMisspellingClickHandler = (
  troubleWords: Array<{ correct_spelling: string; misspellings: string[] }>,
  markdown: string,
  jotdocId: string,
  openModal: (modalType: ModalType, modalProps?: Record<string, any>) => void,
  MODAL_TYPE: ModalType
) => {
  return (word: string) => {
    if (!troubleWords) return;
    
    // Find trouble word entry
    const troubleWordEntry = troubleWords.find(
      entry =>
        entry.correct_spelling.toLowerCase() === word.toLowerCase() ||
        entry.misspellings.some(m => m.toLowerCase() === word.toLowerCase())
    );
    
    if (troubleWordEntry) {
      const isMisspelling = troubleWordEntry.misspellings.some(
        m => m.toLowerCase() === word.toLowerCase()
      );
      
      // Open JotDoc find/replace modal with prefilled values
      openModal(MODAL_TYPE, {
        markdown,
        jotdocId,
        prefillFind: word,
        ...(isMisspelling && { prefillReplace: troubleWordEntry.correct_spelling })
      });
    }
  };
};

